import type {
  DataAlterationGuardName,
  DataAlterationGuardOptions,
  DataAlterationGuardResult,
  DataAlterationGuardVerdict,
} from './data-alteration-guard.types'

import { ALLOWED, GUARD_MARKER } from './data-alteration-guard.constants'
import { v4 as generateUuid } from 'uuid'

const publishedGuards = new Set<DataAlterationGuardName>()

// Asks whether the user accepts that this change outdates the reviews of the
// template.
export function askUnreviewConfirmation(
  options?: DataAlterationGuardOptions
): Promise<DataAlterationGuardVerdict> {
  return guardDataAlteration({ guard: 'unreview-confirmation', options })
}

// One channel serves every caller, so the request carries a uuid. The wait has
// no deadline: the user reads the modal for as long as they like.
export function guardDataAlteration({
  guard,
  options,
}: {
  guard: DataAlterationGuardName
  options?: DataAlterationGuardOptions
}): Promise<DataAlterationGuardVerdict> {
  // Nobody is mounted to answer this guard. The policy that mounts the decider
  // also decides who may write, so a caller that reaches here may proceed.
  if (!guardIsPublished(guard)) return Promise.resolve(ALLOWED)

  const requestId = generateUuid()

  return new Promise((resolve) => {
    const receiveVerdict = (result: DataAlterationGuardResult): void => {
      if (result.requestId !== requestId) return

      window.eventHub.$off('data-alteration-guard-result', receiveVerdict)
      resolve({
        confirmed: result.confirmed,
        skipUnprepare: result.skipUnprepare,
      })
    }

    window.eventHub.$on('data-alteration-guard-result', receiveVerdict)
    window.eventHub.$emit('data-alteration-guard-request', {
      requestId,
      guard,
      options,
    })
  })
}

function guardIsPublished(guard: DataAlterationGuardName): boolean {
  return (document.documentElement.dataset[GUARD_MARKER] ?? '')
    .split(' ')
    .includes(guard)
}

export function publishGuard(guard: DataAlterationGuardName): void {
  publishedGuards.add(guard)
  writeMarker()
}

export function withdrawGuard(guard: DataAlterationGuardName): void {
  publishedGuards.delete(guard)
  writeMarker()
}

// The marker lists every guard that has a decider, so publishing one must not
// drop another. A reader looks for its own name in the list.
function writeMarker(): void {
  if (publishedGuards.size === 0) {
    delete document.documentElement.dataset[GUARD_MARKER]
    return
  }

  document.documentElement.dataset[GUARD_MARKER] = [...publishedGuards].join(
    ' '
  )
}
